import React from 'react';
import { Calendar, Clock, CheckCircle, User, Bell } from 'lucide-react';

const IsometricOnlineBooking = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />

                        {/* Calendar Grid Pattern on Base */}
                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '35px 35px',
                                backgroundPosition: '17.5px 17.5px'
                            }}
                        />
                    </div>
                </div>

                {/* Surface Elements (Flat on the board) */}

                {/* Clock (Top Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '30px',
                        top: '30px',
                        width: '70px',
                        height: '70px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-green-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-green-500 rounded-full flex items-center justify-center shadow-sm border border-green-400">
                        <Clock className="w-8 h-8 text-white" />
                    </div>
                </div>

                {/* User/Profile (Bottom Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '30px',
                        bottom: '30px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-orange-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-500 rounded-2xl flex items-center justify-center shadow-sm border border-orange-400">
                        <User className="w-8 h-8 text-white" />
                    </div>
                </div>


                {/* Floating Elements */}

                {/* Checkmark Bubble (Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '120px',
                        top: '-10px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(60px) rotateZ(-3deg) rotateX(-90deg)', // Standing up
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-blue-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg border border-blue-300">
                        <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                </div>

                {/* Notification Bell (Right) */}
                <div
                    className="absolute"
                    style={{
                        left: '180px',
                        top: '20px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-yellow-600" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-yellow-400 rounded-xl flex items-center justify-center shadow-lg border border-yellow-200">
                        <Bell className="w-8 h-8 text-yellow-900" />
                    </div>
                </div>

                {/* MAIN ELEMENT: Digital Calendar */}
                <div
                    className="absolute"
                    style={{
                        left: '10%',
                        top: '100%',
                        width: '160px',
                        height: '160px',
                        marginLeft: '-90px',
                        marginTop: '-70px',
                        transform: 'translateZ(130px) rotateZ(-65deg) rotateX(-90deg) rotateY(25deg)', // Angled phone
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Calendar Thickness */}
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-gray-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Calendar Front */}
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
                        {/* Month Header (Red) */}
                        <div className="h-12 bg-red-500 flex items-center justify-center">
                            <span className="text-white font-bold text-lg tracking-wide">NOV</span>
                        </div>

                        {/* Calendar Body */}
                        <div className="flex-1 p-3 bg-white flex flex-col items-center justify-center relative">
                            <span className="text-6xl font-bold text-gray-800">20</span>
                            <span className="text-gray-500 font-medium mt-1">Wednesday</span>

                            {/* "Booked" Badge */}
                            <div className="absolute bottom-3 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full border border-green-200 flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                BOOKED
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IsometricOnlineBooking;
